// @ts-nocheck
import { unwrapResult } from '@reduxjs/toolkit';
import { LogInIcon, SuggestionIcon } from 'components/icons';
import { ReactComponent as RegisterImage } from 'components/icons/image-registerPage.svg';
import { LayoutPublic } from 'components/layout';
import { ButtonCTA, LoadingSpinner, TextField } from 'components/ui';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAppDispatch } from 'store';
import { register, userDetailsState } from 'store/userDetailsSlice';
import { usePageTitle, useParseURLQueryParams } from 'utils';

export default function RegisterPage() {
  usePageTitle('Register | Segments');

  const passwordFieldRef = useRef(null);
  const referralCodeRef = useRef(null);
  const dispatch = useAppDispatch();
  const { loading } = useSelector(userDetailsState);
  const { query } = useParseURLQueryParams();
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (query.ref) {
      referralCodeRef.current.value = query.ref;
    }
  }, [query.ref]);

  /** @param {*} event */
  function handleOnSubmit(event) {
    event.preventDefault();
    const { email, password, name, referral } = event.target;
    const formState = {
      email: email.value.toLowerCase(),
      password: password.value,
      name: name.value,
      referral_code: referral.value,
    };

    dispatch(register(formState))
      .then(unwrapResult)
      .catch((/** @type {{ message: string; }} */ err) => {
        if (err.message === '403') {
          toast.error(
            'An account already exists for this email. Log in instead.'
          );
          return;
        }
        toast.error('Failed to perform the task.');
      });
  }

  useEffect(() => {
    const inputs = document.querySelectorAll('input');

    inputs.forEach((input) => {
      input.addEventListener(
        'invalid',
        () => {
          input.classList.add(
            'focus:ring-segments-at-risk',
            'focus:border-segments-at-risk'
          );
        },
        false
      );
      input.addEventListener('keyup', (e) => {
        const capslock = e.getModifierState?.('CapsLock');
        if (capslock) {
          setErrorMsg('Note : Your Capslock key is currently turned on');
        } else {
          setErrorMsg('');
        }
      });
    });
  }, []);

  return (
    <LayoutPublic>
      <main className="relative w-full px-4 mx-auto my-10 lg:pl-32 lg:my-20">
        <div className="flex justify-between">
          <div className="flex items-center">
            <img
              src="/assets/logo-segments.png"
              alt="logo"
              className="w-8 h-8 md:w-10 md:h-10"
            />
            <h2 className="ml-3 text-2xl font-normal leading-7 tracking-segments_logo text-segments-dark">
              Segments
            </h2>
          </div>
          <div className="md:pr-32">
            <Link
              to="/login"
              className="block px-5 py-2 text-black bg-segments-gray rounded-segments-32px text-segments-13px focus:outline-none"
            >
              <div className="flex items-center space-x-2">
                <LogInIcon className="w-4 h-4 text-black" />
                <span className="font-bold">Log in</span>
              </div>
            </Link>
          </div>
        </div>
        <div className="lg:grid lg:grid-cols-5">
          <div className="col-span-2">
            <div className="mt-12">
              <h2 className="text-2xl font-bold leading-9">
                Create an account
              </h2>
              <div className="w-full mt-2 md:w-segments-411px">
                <p className="text-base">
                  Sign up for Segments to uncover hidden data insights, take
                  action with segmentation, and grow your business with data!
                </p>
                <div className="flex items-center p-5 mt-2 space-x-1 bg-segments-gray-F8 rounded-segments-10px">
                  <div className="mr-1">
                    <SuggestionIcon className="w-5 h-5" />
                  </div>
                  <p className="leading-4 opacity-50 text-segments-13px">
                    It takes less than 2 minutes from start to finish, or
                    install directly from the Shopify App Store.
                  </p>
                </div>
              </div>
            </div>
            <form
              className="mt-8 lg:w-segments-411px"
              onSubmit={handleOnSubmit}
              spellCheck={false}
            >
              <div>
                <label htmlFor="name" className="block text-sm font-bold">
                  Name
                </label>
                <TextField
                  name="name"
                  type="text"
                  placeholder="Required"
                  size="small"
                  required
                />
              </div>

              <div className="w-full mt-4 ">
                <label htmlFor="email" className="block text-sm font-bold">
                  Email
                </label>
                <TextField
                  name="email"
                  type="email"
                  placeholder="Required"
                  required
                />
                {errorMsg && (
                  <p className="text-segments-13px text-segments-at-risk">
                    {errorMsg}
                  </p>
                )}
              </div>

              <div className="mt-4">
                <label htmlFor="password" className="block text-sm font-bold">
                  Password
                </label>
                <TextField
                  name="password"
                  type="password"
                  ref={passwordFieldRef}
                  minLength={5}
                  maxLength={128}
                  placeholder="Required"
                  required
                />
              </div>

              <div className="mt-4">
                <label htmlFor="email" className="block text-sm font-bold">
                  Referral code
                </label>
                <TextField
                  ref={referralCodeRef}
                  name="referral"
                  type="text"
                  placeholder="Optional"
                />
              </div>

              <div className="items-center mt-8 md:flex">
                <p className="text-segments-13px">
                  By registering, you agree to our{' '}
                  <a
                    href="https://tresl.co/terms"
                    className="font-bold text-segments-link"
                  >
                    Terms of Service
                  </a>{' '}
                  and{' '}
                  <a
                    href="https://tresl.co/privacy"
                    className="font-bold text-segments-link"
                  >
                    Privacy Policy
                  </a>
                </p>
                <div className="mt-5 lg:mt-0">
                  <ButtonCTA variant="main" type="submit" disabled={loading}>
                    <div className="flex items-center justify-center w-full md:w-32">
                      {loading ? (
                        <LoadingSpinner className="w-5 h-5 text-white" />
                      ) : (
                        'Register'
                      )}
                    </div>
                  </ButtonCTA>
                </div>
              </div>
            </form>
          </div>
          <div className="self-center hidden col-span-3 lg:block justify-self-center">
            <RegisterImage />
          </div>
        </div>
      </main>
    </LayoutPublic>
  );
}
